import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Menu } from 'lucide-react';

import { Collapsible } from '@/components/Collapsible';
import {
  SETTINGS_EXIT_LINK,
  SETTINGS_NAV_GROUPS,
  currentSettingsItem,
} from '@/components/layout/navigation';
import { SettingsSearchBox } from './SettingsSearchBox';

/**
 * Settings shell (SPEC §6).
 *
 * The sub-navigation is `SETTINGS_NAV_GROUPS` — five labelled groups instead
 * of the flat list of thirteen it used to be — with the search box above it
 * and the one way out of the area below it.
 *
 * The group headings are labels, not doors: no GROUP collapses, because the
 * owner has to be able to see every setting the app has without opening
 * anything. See navigation.ts for why Categories and Products are no longer
 * in the list at all.
 *
 * ON A PHONE THE WHOLE NAV FOLDS (owner, 2026-09-23: "the setting tab is
 * completely unusable", iPhone and Samsung alike). The wrapping pill rows it
 * used to become were ~620px tall on a 375px phone, and because the page
 * underneath was a flex item that shrinks, the settings form got whatever was
 * left — which on a phone was nothing: the menu was the only thing on screen.
 * Now it is one button naming the page you are on; it opens the grouped list
 * (all of it, still unfolded group by group) and shuts again the moment it has
 * taken you somewhere. The desktop column is unchanged.
 */

/** How long to keep looking for a jumped-to section before giving up. */
const ANCHOR_WAIT_MS = 8_000;

/**
 * Scrolls to `#set-…` after a settings-search hit.
 *
 * A browser scrolls to a fragment on load; a client-side router does not, and
 * here it could not anyway — every settings page renders "loading…" until its
 * GET lands, so at the moment the URL changes the target element does not yet
 * exist. Hence the retry: look each frame until the section appears, then give
 * up rather than spin if the anchor is genuinely gone.
 *
 * The budget is WALL-CLOCK, not a frame count. A frame count is really a bet
 * on the frame rate, and the case it has to survive is the hosted API waking
 * up cold, where the GET behind the page can take several seconds — a second's
 * worth of frames expires first and the jump silently does nothing. It is also
 * why the deadline is generous: the only cost of waiting is a poll on an id
 * that never arrives, and the only cost of giving up early is a search result
 * that lands on the top of the page with no sign anything was missed.
 *
 * `location.key` is in the dependency list so that searching for the SAME
 * setting twice still scrolls — the hash would be unchanged, but the key is
 * new on every push.
 */
function useAnchorScroll(): void {
  const { hash, key } = useLocation();

  useEffect(() => {
    if (!hash) return;

    const id = hash.slice(1);
    let frame = 0;
    const deadline = Date.now() + ANCHOR_WAIT_MS;

    const tick = (): void => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ block: 'start' });
        return;
      }
      if (Date.now() > deadline) return;
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [hash, key]);
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  [
    // Finger-height rows in the phone menu; the desktop column keeps its
    // compact `block` rows.
    'block rounded-control px-3 py-2 text-sm transition-colors compact:flex compact:min-h-11 compact:items-center',
    // Same "powered" active treatment as the main rail: cyan on a faint
    // cyan fill with a live hairline inset — not a filled block, which
    // competed with each page's actual primary action.
    isActive
      ? 'bg-primary/10 font-medium text-primary shadow-[inset_0_0_0_1px_var(--border-live)]'
      : 'text-content-secondary hover:bg-hover',
  ].join(' ');

/**
 * The settings sub-navigation: a side column on a desktop, one folding menu
 * button on a phone (either way up — see `compact` in styles/responsive.css).
 *
 * `menuOpen` is the phone menu's state and belongs to the layout, so that a
 * link inside can fold it on the way out. It means nothing on a desktop,
 * where the fold renders `display: contents` and the column is the column.
 */
export function SettingsNav({
  menuOpen,
  onMenuOpenChange,
}: {
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const current = currentSettingsItem(location.pathname);
  const close = () => onMenuOpenChange(false);
  // Stable, because the search box subscribes its "/" shortcut with it.
  const reveal = useCallback(() => onMenuOpenChange(true), [onMenuOpenChange]);

  return (
    <nav
      aria-label={t('settings.title')}
      className="w-[var(--sidebar-sub-width)] shrink-0 overflow-y-auto border-e border-subtle p-3 compact:w-full compact:overflow-visible compact:border-e-0 compact:border-b compact:px-4 compact:py-2"
    >
      <Collapsible
        block
        icon={Menu}
        caption={t('settings.title')}
        label={current ? t(current.translationKey) : t('layout.settingsMenu.noPage')}
        open={menuOpen}
        onOpenChange={onMenuOpenChange}
        // Belt and braces with the per-link `onClick`: a jump from the search
        // box is a `navigate()`, not a link click, and must fold the menu too.
        collapseOn={location.key}
        layout="block"
        // Open, the menu never takes more than 70% of what is visible, so the
        // page it is about to replace stays in view under it.
        panelClassName="mt-2 max-h-[70vh] overflow-y-auto overscroll-contain supports-[height:1dvh]:max-h-[70dvh]"
      >
        {/* A plain title, no longer the small-caps label it was: the group
            headings below now own that treatment, and two levels of identical
            grey small-caps would read as one flat list again. On a phone the
            menu button already says "Settings". */}
        <h1 className="mb-3 px-3 text-sm font-semibold text-content compact:hidden">
          {t('settings.title')}
        </h1>

        <SettingsSearchBox onReveal={reveal} />

        <div className="space-y-4 compact:space-y-3">
          {SETTINGS_NAV_GROUPS.map((group) => (
            <div key={group.id}>
              {/* The main rail's own group-heading treatment, so the two
                  levels of navigation read as the same idea. */}
              <h2 className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-content-muted compact:px-1">
                {t(group.translationKey)}
              </h2>
              {/* Phone: an even grid, not wrapping pills — equal targets, and
                  every label on one line. */}
              <div className="compact:grid compact:grid-cols-2 compact:gap-1 compact:sm:grid-cols-3">
                {group.items.map((item) => (
                  <NavLink key={item.id} to={item.path} className={linkClass} onClick={close}>
                    {t(item.translationKey)}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Outside the groups, and styled as a way out rather than as another
            setting: the cafe menu is where Categories and Products went. */}
        <hr className="my-3 border-subtle" />
        <Link
          to={SETTINGS_EXIT_LINK.path}
          onClick={close}
          className="flex items-center gap-2 rounded-control px-3 py-2 text-sm text-content-secondary transition-colors hover:bg-hover compact:min-h-11"
        >
          {t(SETTINGS_EXIT_LINK.translationKey)}
          <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden />
        </Link>
      </Collapsible>
    </nav>
  );
}

export function SettingsLayout() {
  useAnchorScroll();
  // Shut on every arrival: the menu is a way to a page, not a place to stay.
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    /*
     * Desktop: the sub-nav as a side column. Phone: the nav on top as one
     * folding menu button (see SettingsNav), and the page gets the rest of
     * the screen — full width, so every form is one column.
     */
    <div className="flex h-full min-h-0 compact:flex-col">
      <SettingsNav menuOpen={menuOpen} onMenuOpenChange={setMenuOpen} />

      {/*
        Phone: every settings page pads itself `p-6`, which on a 375px screen
        is 48px of a form's width spent on gutters — one override here rather
        than fifteen page edits (SaveBar bleeds by the same 16px to match).
        `scroll-pb-24` keeps a field that focus or a search jump scrolls to
        clear of the sticky Confirm bar instead of landing underneath it.
      */}
      <div className="min-w-0 flex-1 overflow-auto compact:scroll-pb-24 compact:[&>*]:p-4">
        <Outlet />
      </div>
    </div>
  );
}
